// @ts-nocheck
import React, { useState, useEffect } from 'react';
import CustomerTicketList from './CustomerTicketList';
import NewTicketForm from './NewTicketForm';
import TicketModal from './TicketModal';
import CustomerFilters from './CustomerFilters';
import ErrorBoundary from './ErrorBoundary';
import { TicketListLoading, EmptyTicketState, ErrorState } from './LoadingStates';
import { useErrorHandler } from './hooks/useErrorHandler';
import { getErrorMessage } from '../../utils/errorHandling';
import { useSupportHistory } from 'core';
import { isDevMode } from 'lib/featureFlags';

/* Was imported from mockData, though it is a filter shape rather than
   fixture data — the only thing that file held which described the app
   rather than inventing content for it. */
const defaultFilterState = {
  search: '',
  status: [] as string[],
  priority: [] as string[],
  assignedAgent: [] as string[],
  dateRange: { start: null as string | null, end: null as string | null },
  category: [] as string[],
  tags: [] as string[],
};

const CustomerView = ({ userId = 'cust-001' }) => {
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [showNewTicketForm, setShowNewTicketForm] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filters, setFilters] = useState(defaultFilterState);

    const { data: supportData, isFetching: supportLoading, isError, refetch } = useSupportHistory();

    // Error handling
    const { error, isRetrying, retryCount, handleError, clearError, retry, canRetry } = useErrorHandler();

    useEffect(() => {
        console.log("Support Data: ", supportData);
    }, [supportData])

    // Apply filters whenever API data, search query, or filters change
    useEffect(() => {
        const tickets = supportData?.data || [];
        
        // Apply search filter
        let filtered = [...tickets];
        
        if (searchQuery) {
            const searchLower = searchQuery.toLowerCase();
            filtered = filtered.filter(ticket =>
                ticket.id?.toLowerCase().includes(searchLower) ||
                ticket.subject?.toLowerCase().includes(searchLower) ||
                ticket.description?.toLowerCase().includes(searchLower) ||
                ticket.status?.toLowerCase().includes(searchLower)
            );
        }

        // Apply status filter
        if (filters.status && filters.status.length > 0) {
            filtered = filtered.filter(ticket => 
                filters.status.some(status => 
                    ticket.status?.toLowerCase() === status.toLowerCase()
                )
            );
        }

        setFilteredTickets(filtered);
    }, [supportData, searchQuery, filters]);
    const handleNewTicket = (ticketData) => {
        // In a real app, this would make an API call to create ticket
        // For now, just refresh the support data
        console.log('New ticket created:', ticketData);
        refetch(); // Refresh the ticket list from API
        setShowNewTicketForm(false);
    };

    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
    };

    const handleTicketUpdate = (updatedTicket) => {
        // In a real app, this would make an API call to update ticket
        // For now, just refresh the support data
        console.log('Ticket updated:', updatedTicket);
        refetch(); // Refresh the ticket list from API
        setSelectedTicket(updatedTicket);
    };

    // Handle loading state
    if (supportLoading || isRetrying) {
        return (
            <TicketListLoading
                message={isRetrying ? `Retrying... (${retryCount}/3)` : ''}
            />
        );
    };

    // Handle error state
    if (isError && !supportLoading) {
        return (
            <ErrorState
                error={getErrorMessage(error, 'tickets')}
                onRetry={canRetry ? () => retry(refetch) : null}
                title="Unable to load your tickets"
                showRetry={canRetry}
            />
        );
    };

    return (
        <>
            <ErrorBoundary>
                <div className="space-y-4">
                    {/* Enhanced Customer Filters */}
                    {
                        isDevMode() &&
                        <CustomerFilters
                            filters={filters}
                            onFiltersChange={setFilters}
                            onClearFilters={() => {
                                setFilters(defaultFilterState);
                                setSearchQuery('');
                            }}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            ticketCount={filteredTickets.length}
                            totalCount={supportData?.data?.length || 0}
                            tickets={supportData?.data || []}
                        />
                    }


                    {/* Tickets List or Empty State */}
                    {filteredTickets.length === 0 ? (
                        <EmptyTicketState
                            userRole="customer"
                            hasFilters={
                                searchQuery ||
                                Object.values(filters).some(f =>
                                    Array.isArray(f) ? f.length > 0 : f && f !== ''
                                )
                            }
                            onClearFilters={() => {
                                setFilters(defaultFilterState);
                                setSearchQuery('');
                            }}
                            onCreateTicket={() => setShowNewTicketForm(true)}
                        />
                    ) : (
                        <CustomerTicketList
                            loading={supportLoading}
                            supportData={{ ...supportData, data: filteredTickets }}
                            tickets={filteredTickets}
                            onTicketClick={handleTicketClick}
                        />
                    )}

                    {/* New Ticket Form Modal */}
                    {showNewTicketForm && (
                        <NewTicketForm
                            onSubmit={handleNewTicket}
                            onCancel={() => setShowNewTicketForm(false)}
                        />
                    )}

                    {/* Ticket Detail Modal */}
                    {selectedTicket && (
                        <TicketModal
                            ticket={selectedTicket}
                            onClose={() => setSelectedTicket(null)}
                            onUpdate={handleTicketUpdate}
                            userRole="customer"
                            userId={userId}
                        />
                    )}
                </div>
            </ErrorBoundary>
        </>
    );
};

export default CustomerView;